import { useEffect, useContext, useMemo, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import getValidationMessage from 'helpers/getValidationMessage';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

import {
  addMissingMandatoryField,
  removeMissingMandatoryField,
  addRemark,
  removeRemark,
  updateRemark,
  addFile,
  removeFile,
} from 'reducers/autoPersonalFormReducer';

import { getFieldId } from '../resolverUtils';
import type {
  UseLapseResolverConfig,
  LapseFieldIds,
  LapseResolverState,
  LapseResolverHandlers,
} from '../types';

export const CANCELLATION_REASONS = [
  { value: 'non_payment', label: 'Non-payment' },
  { value: 'non_disclosure', label: 'Non-disclosure' },
  { value: 'misrepresentation', label: 'Misrepresentation' },
  { value: 'other', label: 'Other (Please explain the reason for the lapse)' },
] as const;

export const useLapseResolver = (config: UseLapseResolverConfig) => {
  const { validationId, section, itemKey, formStateKey, loeFileType } = config;

  const { validations } = useContext(ApplicationFormContext);
  const dispatch = useAppDispatch();

  const mandatoryFileValidations = getMandatoryFileValidations(formStateKey);

  const alertMessage = getValidationMessage(validations, validationId);
  const show = !!alertMessage;

  const remark = useAppSelector((state) => {
    return state[formStateKey].remarks.find(
      (r: any) => r.validation_key === validationId && r.item_key === itemKey,
    );
  });

  const file = useAppSelector((state) => {
    return state[formStateKey].files.find(
      (f: any) => f.validation_key === validationId && f.item_key === itemKey,
    );
  });

  const customData =
    remark?.custom_data?.type === 'lapse' ? remark.custom_data : null;
  const cancellationReason = customData?.cancellation_reason || null;
  const otherExplanation = customData?.other_explanation || '';
  const noClaimsConfirmed = customData?.no_claims_confirmed || false;

  // Generate field IDs for mandatory field tracking and navigation
  const fieldIds: LapseFieldIds = useMemo(
    () => ({
      cancellationReason: getFieldId('Lapse Cancellation Reason', itemKey),
      otherExplanation: getFieldId('Lapse Other Explanation', itemKey),
      noClaimsConfirmed: getFieldId('Lapse No Claims Confirmation', itemKey),
      remarks: getFieldId('Lapse Remarks', itemKey),
    }),
    [itemKey],
  );

  // Initialize remark and file when validation is shown
  useEffect(() => {
    if (show) {
      dispatch(
        addRemark({
          validation_key: validationId,
          item_key: itemKey,
          text: '',
          status: 'created',
          text_validator_log_id: null,
          ai_result: '',
          section,
          custom_data: {
            type: 'lapse',
            cancellation_reason: null,
            other_explanation: null,
            no_claims_confirmed: false,
          },
        }),
      );

      // Add file for LOE upload
      dispatch(
        addFile({
          validation_key: validationId,
          item_key: itemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type: loeFileType,
          files: [],
          text: 'Letter of Experience',
          mandatory: mandatoryFileValidations.includes(validationId),
        }),
      );
    } else {
      dispatch(
        removeRemark({
          validation_key: validationId,
          item_key: itemKey,
        }),
      );
      dispatch(
        removeFile({
          validation_key: validationId,
          item_key: itemKey,
        }),
      );
    }
  }, [
    show,
    validationId,
    itemKey,
    section,
    loeFileType,
    mandatoryFileValidations,
    dispatch,
  ]);

  // Manage mandatory field validation
  useEffect(() => {
    if (!show || !remark) {
      dispatch(removeMissingMandatoryField(fieldIds.cancellationReason));
      dispatch(removeMissingMandatoryField(fieldIds.otherExplanation));
      dispatch(removeMissingMandatoryField(fieldIds.noClaimsConfirmed));
      dispatch(removeMissingMandatoryField(fieldIds.remarks));
      return;
    }

    // Cancellation reason validation
    if (!cancellationReason) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.cancellationReason,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.cancellationReason));
    }

    // Other explanation validation (only when "other" is selected)
    if (
      cancellationReason === 'other' &&
      (!otherExplanation || otherExplanation.trim().length === 0)
    ) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.otherExplanation,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.otherExplanation));
    }

    // No claims confirmation validation
    if (!noClaimsConfirmed) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.noClaimsConfirmed,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.noClaimsConfirmed));
    }

    // Remarks validation
    if (!remark.text || remark.text.trim().length === 0) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.remarks,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.remarks));
    }
  }, [
    show,
    remark,
    cancellationReason,
    otherExplanation,
    noClaimsConfirmed,
    section,
    fieldIds,
    dispatch,
  ]);

  // Handlers
  const handleCancellationReasonChange = useCallback(
    (value: string) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              type: 'lapse',
              cancellation_reason: value as
                | 'non_payment'
                | 'non_disclosure'
                | 'misrepresentation'
                | 'other',
              other_explanation:
                value === 'other' ? customData?.other_explanation : null,
              no_claims_confirmed: customData?.no_claims_confirmed || false,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleOtherExplanationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              ...customData,
              type: 'lapse',
              other_explanation: e.target.value,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleNoClaimsConfirmedChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              ...customData,
              type: 'lapse',
              no_claims_confirmed: e.target.checked,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleRemarksChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            text: e.target.value,
            status: 'created',
          },
        }),
      );
    },
    [dispatch, validationId, itemKey],
  );

  // Determine compact alert status based on completion state
  const getCompactAlertStatus = useCallback(() => {
    if (!remark) return 'warning';
    if (!cancellationReason) return 'warning';
    if (cancellationReason === 'other' && !otherExplanation?.trim())
      return 'warning';
    if (!noClaimsConfirmed) return 'warning';
    if (!remark.text?.trim()) return 'warning';
    if (remark.status !== 'approved') return 'warning';
    if (file && file.status !== 'approved' && !file.is_deferred)
      return 'warning';
    return 'success';
  }, [remark, file, cancellationReason, otherExplanation, noClaimsConfirmed]);

  const state: LapseResolverState = {
    remark,
    file,
    cancellationReason,
    otherExplanation,
    noClaimsConfirmed,
    show,
  };

  const handlers: LapseResolverHandlers = {
    handleCancellationReasonChange,
    handleOtherExplanationChange,
    handleNoClaimsConfirmedChange,
    handleRemarksChange,
  };

  return {
    state,
    handlers,
    fieldIds,
    alertMessage,
    getCompactAlertStatus,
  };
};
